/* eslint-disable no-unused-vars */
import React, { useState } from "react";
import { Form, Select, Button, Spin, message, Input, Modal, Upload, Tabs } from "antd";
import { ExclamationCircleOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import API from "../../api";
import urls from "../../api/urls";
import back_img from "../../assests/images/rightVector.svg";
import Smcs_Loader from "../../assests/images/loader-new.gif";
import constants from "../../constants/constants";
import { checkSpace, checkSpecialCharacter, getUserAndPremiseId } from "../../utils";

const { Option } = Select;
const { TextArea } = Input;

// Parse comma/newline/space-separated AWBs into a clean array
const parseAwbs = (raw) =>
  raw
    .split(/[\n,\s]+/)
    .map((s) => s.trim())
    .filter(Boolean);

const normFile = (e) => {
  if (Array.isArray(e)) {
    return e;
  }
  return e?.fileList;
};

function MarkLostDamage() {
  const navigate = useNavigate();
  const [markForm] = Form.useForm();
  const [inscanForm] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("mark");

  const userinfo = JSON.parse(localStorage.getItem("userinfo") || "{}");
  const userRoles = Array.isArray(userinfo?.roles) ? userinfo.roles : [];
  const isHoAdmin = userRoles.includes(constants.ROLES.HO_ADMIN);
  const isDeveloper = userRoles.includes(constants.ROLES.DEVELOPER);
  const canUseInscan = isHoAdmin || isDeveloper;

  const submitToApi = async (values, awb) => {
    const userInfo = JSON.parse(localStorage.getItem("userId") || "{}");
    const premiseId = userInfo?.userInfo?.premiseId;
    const userId = userInfo?.userInfo?.userId;

    setLoading(true);
    try {
      const finalRemark = values.status === "ON_HOLD" && values.reason ? values.reason : values.remark?.trim();
      const payload = {
        awbNumbers: [awb],
        status: values.status,
        premiseId,
        userId,
        ...(finalRemark ? { remark: finalRemark } : {}),
      };

      const formData = new FormData();
      formData.append(
        "data",
        new Blob([JSON.stringify(payload)], { type: "application/json" })
      );
      const fileList = values.attachment || [];
      const file = fileList[0]?.originFileObj;
      if (file) {
        formData.append("attachment", file);
      }

      const response = await API.Api(urls.MARK_SHIPMENT_LOST_DAMAGE).postIdHeaderFormData(
        formData
      );
      const resData = response?.response?.data;
      const payloadStatus = resData?.status;

      if (payloadStatus === 200 || payloadStatus === 207) {
        const successSet = new Set(resData?.successfulAwbs || []);
        const failed = (resData?.failures || []).find((f) => f.awbNumber === awb);
        const statusLabel = values.status.toLowerCase();

        if (successSet.has(awb)) {
          message.success(`Shipment ${awb} marked as ${statusLabel} successfully.`);
          markForm.setFieldsValue({ awbNumbers: "", remark: "", reason: undefined, attachment: [] });
        } else if (failed?.reason) {
          message.error({
            content: `Could not mark shipment ${awb} as ${statusLabel}: ${failed.reason}`,
            duration: 8,
          });
        } else {
          message.error({
            content: `Could not mark shipment ${awb} as ${statusLabel}. Please try again.`,
            duration: 6,
          });
        }
      } else {
        message.error(
          resData?.message || "Failed to update shipment status. Please try again."
        );
      }
    } catch {
      message.error("Network error. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const onFinishMark = (values) => {
    const awbNumbers = parseAwbs(values.awbNumbers);
    if (awbNumbers.length === 0) {
      message.warning("Please enter an AWB number.");
      return;
    }
    if (awbNumbers.length > 1) {
      message.warning("Only one AWB number is allowed per request.");
      return;
    }

    const awb = awbNumbers[0];
    const statusLabel = values.status === "LOST" ? "Lost" : values.status === "DAMAGED" ? "Damaged" : "On Hold";

    let iconColor = "#fa8c16"; // Damaged default
    if (values.status === "LOST") iconColor = "#ff4d4f";
    if (values.status === "ON_HOLD") iconColor = "#1677ff";

    Modal.confirm({
      title: `Mark shipment as ${statusLabel}?`,
      icon: <ExclamationCircleOutlined style={{ color: iconColor }} />,
      content: (
        <div style={{ paddingTop: 4 }}>
          <p style={{ margin: "0 0 8px", color: "#595959" }}>
            You are about to mark shipment <strong>{awb}</strong> as{" "}
            <strong style={{ color: iconColor }}>
              {statusLabel}
            </strong>. This action cannot be undone.
          </p>
          {values.reason && (
            <p style={{ margin: "0 0 8px", color: "#8c8c8c", fontSize: 13 }}>
              Reason: <em>{values.reason}</em>
            </p>
          )}
          {values.remark && (
            <p style={{ margin: 0, color: "#8c8c8c", fontSize: 13 }}>
              Remark: <em>{values.remark}</em>
            </p>
          )}
        </div>
      ),
      okText: `Yes, Mark as ${statusLabel}`,
      okButtonProps: {
        danger: values.status === "LOST",
        style: values.status !== "LOST" ? { background: iconColor, borderColor: iconColor } : {},
      },
      cancelText: "Cancel",
      onOk: () => submitToApi(values, awb),
    });
  };

  const submitInscan = async (values) => {
    const awbNumbers = parseAwbs(values.awbNumbers);
    if (awbNumbers.length === 0) {
      message.warning("Please enter at least one AWB number.");
      return;
    }
    if (awbNumbers.length > 1) {
      message.warning("Only one AWB number is allowed per request.");
      return;
    }

    const { premiseId, userId } = getUserAndPremiseId() || {};
    if (!premiseId || !userId) {
      message.error("Could not read premiseId/userId. Please re-login and try again.");
      return;
    }

    Modal.confirm({
      title: "Mark Lost/Damage shipment(s) as Inscan?",
      icon: <ExclamationCircleOutlined style={{ color: "#1677ff" }} />,
      content: (
        <div style={{ paddingTop: 4 }}>
          <p style={{ margin: "0 0 8px", color: "#595959" }}>
            You are about to mark shipment <strong>{awbNumbers[0]}</strong> as{" "}
            <strong>Inscan</strong>.
          </p>
          <p style={{ margin: 0, color: "#8c8c8c", fontSize: 13 }}>
            This will call the lost/damage inscan API for the entered AWB(s).
          </p>
        </div>
      ),
      okText: "Yes, Mark as Inscan",
      cancelText: "Cancel",
      onOk: async () => {
        setLoading(true);
        try {
          const awb = awbNumbers[0];
          const payload = {
            awbNumbers: [awbNumbers[0]],
            premiseId,
            userId,
          };

          const response = await API.Api(urls.MARK_LOST_DAMAGE_INSCAN).post(payload);
          const resData = response?.response?.data;
          const payloadStatus = resData?.status;

          if (payloadStatus === 200 || payloadStatus === 207) {
            const successSet = new Set(resData?.successfulAwbs || []);
            const failed = (resData?.failures || []).find((f) => f.awbNumber === awb);

            if (successSet.has(awb)) {
              message.success(resData?.message || `Shipment ${awb} marked as inscan successfully.`);
              inscanForm.setFieldsValue({ awbNumbers: "" });
            } else if (failed?.reason) {
              message.error({
                content: failed.reason,
                duration: 8,
              });
            } else {
              message.error(resData?.message || "Failed to mark as inscan. Please try again.");
            }
          } else {
            message.error(resData?.message || "Failed to mark as inscan. Please try again.");
          }
        } catch {
          message.error("Network error. Please try again.");
        } finally {
          setLoading(false);
        }
      },
    });
  };

  const tabItems = [
    {
      key: "mark",
      label: "Mark Lost / Damaged/On Hold",
      children: (
        <Spin
          spinning={loading}
          indicator={
            <img
              src={Smcs_Loader}
              style={{ height: "80px", width: "80px" }}
              alt="Loading..."
            />
          }
        >
          <Form
            form={markForm}
            layout="vertical"
            onFinish={onFinishMark}
            autoComplete="off"
            className="global-form"
            style={{ maxWidth: 520 }}
            initialValues={{ status: "LOST" }}
          >
            <Form.Item
              label="AWB Number"
              name="awbNumbers"
              rules={[{ required: true, message: "Please enter the AWB number" }]}
              extra="One AWB per request."
            >
              <Input
                placeholder="Enter AWB number"
                size="large"
                style={{ borderRadius: "8px" }}
                onKeyPress={(e) => {
                  if (checkSpecialCharacter(e) || checkSpace(e)) e.preventDefault();
                }}
              />
            </Form.Item>

            <Form.Item
              label="Status"
              name="status"
              rules={[{ required: true, message: "Please select a status" }]}
            >
              <Select placeholder="Select status" size="large" style={{ borderRadius: "8px" }}>
                <Option value="LOST">Lost</Option>
                <Option value="DAMAGED">Damaged</Option>
                <Option value="ON_HOLD">On Hold</Option>
              </Select>
            </Form.Item>

            <Form.Item
              noStyle
              shouldUpdate={(prevValues, currentValues) => prevValues.status !== currentValues.status}
            >
              {({ getFieldValue }) =>
                getFieldValue("status") === "ON_HOLD" ? (
                  <Form.Item
                    label="Reason"
                    name="reason"
                    rules={[{ required: true, message: "Please select a reason" }]}
                  >
                    <Select placeholder="Select reason" size="large" style={{ borderRadius: "8px" }}>
                      <Option value="Scheduled Weekly Delivery">Scheduled Weekly Delivery</Option>
                      <Option value="Pending Office Collection">Pending Office Collection</Option>
                      <Option value="On Hold – GST Compliance">On Hold – GST Compliance</Option>
                      <Option value="On Hold – Customer Request (Consignee/Consignor)">On Hold – Customer Request (Consignee/Consignor)</Option>
                      <Option value="On Hold – Management Decision">On Hold – Management Decision</Option>
                      <Option value="On Hold – Natural Calamity">On Hold – Natural Calamity</Option>
                      <Option value="On Hold – Regulatory/State Restrictions">On Hold – Regulatory/State Restrictions</Option>
                      <Option value="On Hold – Festival Closure">On Hold – Festival Closure</Option>
                    </Select>
                  </Form.Item>
                ) : null
              }
            </Form.Item>

            <Form.Item
              noStyle
              shouldUpdate={(prevValues, currentValues) => prevValues.status !== currentValues.status}
            >
              {({ getFieldValue }) =>
                getFieldValue("status") !== "ON_HOLD" ? (
                  <Form.Item label="Remark" name="remark">
                    <TextArea
                      placeholder="Optional — reason for marking as lost/damaged"
                      rows={2}
                      maxLength={255}
                      showCount
                      style={{ borderRadius: "8px" }}
                    />
                  </Form.Item>
                ) : null
              }
            </Form.Item>

            <Form.Item
              noStyle
              shouldUpdate={(prevValues, currentValues) => prevValues.status !== currentValues.status}
            >
              {({ getFieldValue }) =>
                getFieldValue("status") !== "ON_HOLD" ? (
                  <Form.Item
                    label="Attachment"
                    name="attachment"
                    required
                    valuePropName="fileList"
                    getValueFromEvent={normFile}
                    rules={[
                      {
                        validator: (_, fileList) => {
                          if (fileList?.length) {
                            return Promise.resolve();
                          }
                          return Promise.reject(new Error("Please upload an attachment"));
                        },
                      },
                    ]}
                    extra="Photo or document sent with the bulk terminal-status request."
                  >
                    <Upload
                      beforeUpload={() => false}
                      maxCount={1}
                      accept="image/*,.pdf"
                      listType="picture"
                    >
                      <Button style={{ borderRadius: "8px" }}>Choose file</Button>
                    </Upload>
                  </Form.Item>
                ) : null
              }
            </Form.Item>

            <Form.Item>
              <Button
                type="primary"
                htmlType="submit"
                size="large"
                loading={loading}
                style={{
                  background: "#175EB6",
                  borderRadius: "8px",
                  fontWeight: "600",
                  paddingInline: 32,
                }}
              >
                Submit
              </Button>
            </Form.Item>
          </Form>
        </Spin>
      ),
    },
  ];

  if (canUseInscan) {
    tabItems.push({
      key: "inscan",
      label: "Lost/Damage → Inscan",
      children: (
        <Spin
          spinning={loading}
          indicator={
            <img
              src={Smcs_Loader}
              style={{ height: "80px", width: "80px" }}
              alt="Loading..."
            />
          }
        >
          <Form
            form={inscanForm}
            layout="vertical"
            onFinish={submitInscan}
            autoComplete="off"
            className="global-form"
            style={{ maxWidth: 520 }}
          >
            <Form.Item
              label="AWB Number"
              name="awbNumbers"
              rules={[{ required: true, message: "Please enter the AWB number" }]}
              extra="One AWB per request."
            >
              <Input
                placeholder="Enter AWB number"
                size="large"
                style={{ borderRadius: "8px" }}
                onKeyPress={(e) => {
                  if (checkSpecialCharacter(e) || checkSpace(e)) e.preventDefault();
                }}
              />
            </Form.Item>

            <Form.Item>
              <Button
                type="primary"
                htmlType="submit"
                size="large"
                loading={loading}
                style={{
                  background: "#175EB6",
                  borderRadius: "8px",
                  fontWeight: "600",
                  paddingInline: 32,
                }}
              >
                Mark as Inscan
              </Button>
            </Form.Item>
          </Form>
        </Spin>
      ),
    });
  }

  return (
    <div style={{ padding: "20px", background: "#F8FAFC" }}>
      <div
        style={{
          background: "#fff",
          padding: "16px 24px",
          borderRadius: "8px",
          boxShadow: "0 1px 2px rgba(16, 24, 40, 0.05)",
        }}
      >
        {/* Header */}
        <div style={{ marginBottom: "24px", display: "flex", alignItems: "center" }}>
          <img
            src={back_img}
            onClick={() => navigate(-1)}
            style={{ cursor: "pointer", marginRight: "8px" }}
            alt="back"
          />
          <h1 style={{ margin: 0, fontSize: "20px", fontWeight: "600", color: "#101828" }}>
            Mark Lost / Damaged/ On Hold
          </h1>
        </div>

        <Tabs
          activeKey={activeTab}
          onChange={setActiveTab}
          items={tabItems}
        />
      </div>
    </div>
  );
}

export default MarkLostDamage;
